import { SCORING } from './constants';
import { SCORE_ROW, resolveSlot } from './scoreRow';

export type ScoreOutcome = 'scored' | 'lost';

export interface ScoreResult {
  outcome: ScoreOutcome;
  /** Pocket id, or null when the ball never came to rest in one. */
  slotId: string | null;
  /** Display label for the pocket, or null when lost. */
  slotLabel: string | null;
  points: number;
  /** The ball's world X when the result was resolved. */
  x: number;
  /** The ball's world Y when the result was resolved. */
  y: number;
}

/**
 * ============================================================================
 * SCORING - one ball, one pocket, one payment
 * ============================================================================
 *
 * A round ends when the ball comes to rest inside a pocket of the scoring row.
 * The pocket is resolved from the ball's X at that moment by
 * `src/game/scoreRow.ts`, which is a pure function of one coordinate.
 *
 * ---------------------------------------------------------------------------
 * WHY "AT REST" AND NOT "AT THE MOMENT IT CROSSES THE MOUTH"
 * ---------------------------------------------------------------------------
 * Resolving at the crossing is tempting - it is one event with no threshold to
 * tune - but it can disagree with what the player sees. A ball that clips a
 * divider on its way in rolls into the neighbouring pocket, and paying it for
 * the pocket it was over at the mouth would put a label on the display that
 * contradicts the ball sitting on the screen.
 *
 * The row is a closed box: a floor, a front lip, a back panel per pocket and a
 * divider on either side. A ball that gets in cannot get out and cannot move
 * between pockets, so "at rest" is both unambiguous and final. The threshold is
 * therefore not delicate - it only has to recognise a ball that has stopped.
 *
 * ---------------------------------------------------------------------------
 * WHY THE TRACKER IS ONE-SHOT
 * ---------------------------------------------------------------------------
 * The whole point of this class is that a ball can award its final score
 * **exactly once**. Once `update()` has resolved a result, every later call is a
 * no-op, so a ball sitting still for ten thousand frames produces one score
 * event rather than ten thousand. That is asserted by the test suite rather
 * than left to the caller's discipline.
 *
 * There is deliberately no "the ball left the board" branch. Leaving the board is
 * the point of the game: the ball is SUPPOSED to roll out of the guard's exit
 * window and fall into the row. The only failure this tracker recognises is a
 * ball that fell past the row's floor without ever being caught by it, which
 * means it missed the machine - and that is checked before the mouth, because a
 * ball that misses has already crossed the mouth's plane.
 */
export class ScoringTracker {
  private restSteps = 0;
  private resolved: ScoreResult | null = null;
  /** True once the ball has crossed the row's mouth on the way down. */
  private inRow = false;

  /** Clear all state. Called on restart. */
  reset(): void {
    this.restSteps = 0;
    this.resolved = null;
    this.inRow = false;
  }

  /** The final result, or null while the round is still live. */
  get result(): ScoreResult | null {
    return this.resolved;
  }

  get isResolved(): boolean {
    return this.resolved !== null;
  }

  /** True once the ball has entered the row, whether or not it has settled. */
  get hasEnteredRow(): boolean {
    return this.inRow;
  }

  /** Consecutive steps the ball has been below the settle speed inside the row. */
  get consecutiveRestSteps(): number {
    return this.restSteps;
  }

  /**
   * Advance one fixed physics step.
   *
   * Returns the result **only on the step that resolved it**, and null
   * otherwise. Callers therefore fire their state transition from a one-shot
   * event and cannot double-count.
   */
  update(x: number, y: number, speed: number): ScoreResult | null {
    if (this.resolved) return null;

    // A ball that has fallen well past the row's floor never entered a pocket:
    // the row's colliders would have caught it. This is checked FIRST because a
    // ball that misses the row has already crossed the mouth's plane, so the
    // latch below would otherwise be set and the tracker would then wait for a
    // ball in free fall to come to rest - which it never does.
    if (y < SCORE_ROW.floorY - SCORING.fallThroughMargin) {
      this.resolved = {
        outcome: 'lost',
        slotId: null,
        slotLabel: null,
        points: SCORING.ballLostPoints,
        x,
        y,
      };
      return this.resolved;
    }

    // The row's mouth is a plane. The ball is in the row once its CENTRE is
    // below it, which is also the plane the dividers' tops sit on - so the
    // moment the ball is between two dividers is the moment it counts as in.
    if (!this.inRow) {
      if (y > SCORE_ROW.mouthY) return null;
      this.inRow = true;
      this.restSteps = 0;
    }

    if (speed >= SCORING.settleSpeed) {
      this.restSteps = 0;
      return null;
    }

    this.restSteps += 1;
    if (this.restSteps < SCORING.settleSteps) return null;

    const slot = resolveSlot(x);
    this.resolved = slot
      ? {
          outcome: 'scored',
          slotId: slot.id,
          slotLabel: slot.label,
          points: slot.points,
          x,
          y,
        }
      : {
          // In the row but between pockets, i.e. balanced on a divider. Any
          // answer would be arbitrary, so the ball is not paid.
          outcome: 'lost',
          slotId: null,
          slotLabel: null,
          points: SCORING.ballLostPoints,
          x,
          y,
        };

    return this.resolved;
  }

  /**
   * End the round without a score, because the clock ran out.
   *
   * Latching, exactly like `update()`: the result is recorded once and every
   * later call is a no-op, so a timed-out round cannot be paid for afterwards.
   *
   * This lives here rather than in `App` so that every round-ending outcome
   * passes through the same one-shot latch. A caller that could construct a
   * `ScoreResult` of its own could award points twice.
   */
  timeout(x: number, y: number): ScoreResult {
    if (this.resolved) return this.resolved;

    this.resolved = {
      outcome: 'lost',
      slotId: null,
      slotLabel: null,
      points: SCORING.ballLostPoints,
      x,
      y,
    };
    return this.resolved;
  }
}
